import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import orderBy from 'lodash/orderBy';
import { Page, TableList, PageEmptyState, PageErrorState, Searchbox, FilterPickerGroup, Button, PageContentHeader, Typography, MultiSelectFilterPicker, ConfirmationModal } from 'seagull';
import { Plus } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { ManageContactModalForVendor } from "../../components/ManageContactModal";
import { routes } from "../../constants";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import { useEnterAccountantAccess } from "../../hooks/useEnterAccountantAccess";
import AccountantAccessLearnMoreButton from "../../components/AccountantAccessLearnMoreButton";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import PageLoadingState from "../../components/PageLoadingState";
import { EmptyStateIllustration } from "./EmptyStateIllustration";
import ContactsDashboardRow from "./ContactsDashboardRow";
import VendorAccountantAccessModal from "./VendorAccountantAccessModal";
import styles from "./ContactsDashboard.module.scss";
import { handleRequest } from "../../infra/utils";
import { useContactActions } from "../../hooks/useContactActions";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { ErrorResponse } from "../../infra/ErrorResponse";
import { ContactStatus } from "../../models/ContactModel";
const title = 'Contacts';
const headerItems = [{
  id: 'companyName',
  value: 'Company name'
}, {
  id: 'status',
  value: 'Status'
}, {
  id: 'primaryContactName',
  value: 'Contact name'
}, {
  id: 'contactDetails',
  value: 'Contact details'
}];
const sortIds = headerItems.map(i => i.id);
const columnDefs = [{
  width: [140, 300],
  sortable: true
},
// Company Name
{
  width: [140, 300],
  sortable: true
},
// Status
{
  width: [170, 170],
  sortable: true
},
// Contact name
{
  width: [160, 300],
  sortable: true
} // Contact details
];
const contactStatusFilterOptions = [{
  value: ContactStatus.Active,
  label: 'Active'
}, {
  value: ContactStatus.Archived,
  label: 'Archived'
}];
export const importContactsRoute = {
  id: 'import-contacts',
  text: 'Import contacts',
  url: routes.importContacts,
  shouldShow: _ref => {
    let {
      isVendor
    } = _ref;
    return isVendor;
  }
};
export function ActualContactsDashboard(props) {
  const {
    data: contacts,
    isRefreshing,
    refresh
  } = props;
  const qboCustomers = useStore(state => state.qboCustomers.data);
  const {
    api,
    fetchQboCustomers,
    logBI,
    fetchIntegrations,
    fetchUserInfo
  } = useStoreActions();
  const {
    reportToGtm,
    reportToAppcues
  } = useEventReporter();
  const {
    startLoader,
    stopLoader,
    showErrorToast,
    showModal
  } = useOverlayContext();
  const defaultSort = useMemo(() => ({
    colIndex: 0,
    sort: 'desc'
  }), []);
  const {
    sort: tableSort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const scrollElRef = useRef(null);
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11602);
  const {
    urlState: {
      search,
      status: contactStatusFilterValue
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    },
    status: {
      defaultValue: [ContactStatus.Active]
    }
  });
  const hasContacts = !!(contacts && contacts.length);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const importContactsLink = useStore(selectors.filterAvailableRoutes([importContactsRoute]))[0];
  const enterAccountantAccess = useEnterAccountantAccess();
  const {
    withdrawToEditContactProposals
  } = useContactActions();
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  useEffect(() => {
    if (showQboIntegration) {
      Promise.all([fetchQboCustomers(), fetchIntegrations()]);
    }
  }, [showQboIntegration]);
  const qboCustomerIdToDisplay = useMemo(() => {
    if (!qboCustomers) return {};
    return qboCustomers.reduce((prev, qboCustomer) => {
      prev[qboCustomer.id()] = qboCustomer.formattedDisplay();
      return prev;
    }, {});
  }, [qboCustomers]);
  const filteredAndSorted = useMemo(() => {
    const filtered = (contacts || []).filter(contact => {
      // search
      const trimmedSearch = search ? search.trim().toLowerCase() : '';
      const termForSearch = "".concat(contact.companyName(), " ").concat(contact.firstName(), " ").concat(contact.lastName(), " ").concat(contact.email());
      const passesSearch = termForSearch.toLowerCase().includes(trimmedSearch);

      // status filter
      const passesStatusFilter = contactStatusFilterValue.includes(contact.status());
      return passesSearch && passesStatusFilter;
    });
    const {
      sort,
      colIndex
    } = tableSort || {
      colIndex: 0,
      sort: 'asc'
    };
    const sortPropertiesByHeaderIndex = [[c => c.companyName()], [s => s.status(), c => c.companyName()], [c => c.firstName(), c => c.lastName(), c => c.companyName()], [c => c.email(), c => c.companyName()]];
    const sortProperties = sortPropertiesByHeaderIndex[colIndex];
    const sortingOrder = sort === 'asc' ? 'desc' : 'asc';
    const orders = Array.from(Array(sortProperties.length)).map(() => sortingOrder);
    return orderBy(filtered, sortProperties, orders);
  }, [contacts, tableSort, search, contactStatusFilterValue]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: filteredAndSorted,
    scrollElRef
  });
  const onSortChange = sort => {
    logBI(11603, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    changeSort(sort);
  };
  const onClearSearch = () => {
    changeUrlState({
      search: ''
    });
    debouncedLogSearchToBI.onClear();
  };
  function onClearFilters() {
    changeUrlState({
      search: '',
      status: contactStatusFilterOptions.map(opt => opt.value)
    });
  }
  const openContactModal = async contact => {
    logBI(11600, {
      contact: contact && contact.toJSON()
    });
    reportToGtm('button-click', 'contacts-table-view-contact');
    reportToAppcues('button-click', 'contacts-table-view-contact');
    setRegisterSearchBoxHotkey(false);
    try {
      const savedContact = await showModal({
        closeOnEscapeKey: true,
        closeOnClickOutside: true,
        content: _ref2 => {
          let {
            dismissModal
          } = _ref2;
          return /*#__PURE__*/React.createElement(ManageContactModalForVendor, {
            "data-testid": "manage-contact-modal",
            existingContact: contact,
            onClose: dismissModal,
            origin: "Contacts dashboard"
          });
        }
      });
      if (savedContact) {
        await refresh();
      }
    } finally {
      setRegisterSearchBoxHotkey(true);
    }
  };
  const onRestoreContact = async contact => {
    logBI(11605, {
      contact: {
        firstName: contact.firstName(),
        lastName: contact.lastName(),
        email: contact.email()
      }
    });
    const loader = 'restore-contact-loader';
    startLoader(loader);
    const result = await handleRequest(api.contact.restore(contact.id(), {
      version: contact.version()
    }));
    if (result.success) {
      await refresh();
    } else {
      showErrorToast(result.error.restoreContactErrorText(), 'restore-error-toast');
    }
    stopLoader(loader);
  };
  const onArchiveContact = async contact => {
    try {
      const shouldWithdrawProposals = contact.hasPendingProposals();
      const confirmed = await showModal({
        content: _ref3 => {
          let {
            dismissModal
          } = _ref3;
          return /*#__PURE__*/React.createElement(ConfirmationModal, {
            skin: "danger",
            title: shouldWithdrawProposals ? 'This client has proposals awaiting approval' : 'Archive client',
            content: shouldWithdrawProposals ? "Archiving ".concat(contact.fullName(), " will automatically withdraw their pending proposals.") : "Are you sure you want to archive ".concat(contact.fullName(), "?"),
            primaryButtonLabel: shouldWithdrawProposals ? 'Archive client and withdraw proposal' : 'Archive client',
            secondaryButtonLabel: "Cancel",
            onPrimaryButtonClick: () => dismissModal(true),
            onSecondaryButtonClick: () => dismissModal(false),
            primaryButtonTestid: shouldWithdrawProposals ? 'confirm-withdraw-and-archive-btn' : undefined
          });
        }
      });
      if (confirmed) {
        logBI(11604, {
          contact: {
            firstName: contact.firstName(),
            lastName: contact.lastName(),
            email: contact.email()
          }
        });
        startLoader('archive-contact-loader');
        if (shouldWithdrawProposals) {
          await withdrawToEditContactProposals(contact.id());
        }
        await api.contact.archive(contact.id(), {
          version: contact.version()
        });
        await refresh();
      }
    } catch (err) {
      showErrorToast(new ErrorResponse(err).archiveContactErrorText(), 'archive-error-toast');
    } finally {
      stopLoader('archive-contact-loader');
    }
  };
  function onRequestAccountantAccess(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/React.createElement(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          primaryButtonLabel: "Request access",
          title: "Request Accountant Access",
          primaryButtonTestid: "request-acc-access-submit",
          content: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, {
            className: styles.requestAccAccessParagraph
          }, "With Accountant Access to your client's business, you can review their information and manage their payment methods."), /*#__PURE__*/React.createElement("ul", {
            className: cn(styles.requestAccAccessParagraph, styles.requestAccAccessList)
          }, ['Your client will receive an email about this request.', 'We will notify you by email when they accept your request.', 'Once approved by your client, you will be able to access their client portal using the Anchor logo next to their contact in your contacts list.'].map(text => /*#__PURE__*/React.createElement("li", {
            key: text
          }, /*#__PURE__*/React.createElement(Typography, null, text)))), /*#__PURE__*/React.createElement(AccountantAccessLearnMoreButton, {
            isVendor: true
          })),
          failureToastProps: {
            text: 'Failed to request Accountant Access',
            testid: 'acc-access-request-failure'
          },
          onSuccess: refresh,
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13100, {
              customerId,
              companyName
            });
            await api.accountants.requestAccess(customerId);
          }
        });
      }
    });
  }
  function onCancelAccountantAccessRequest(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/React.createElement(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          skin: "danger",
          primaryButtonLabel: "Cancel request",
          title: "Cancel Accountant Access request",
          primaryButtonTestid: "cancel-acc-access-request-submit",
          content: /*#__PURE__*/React.createElement(Typography, null, "Are you sure you want to cancel your Accountant Access request to", ' ', /*#__PURE__*/React.createElement(Typography, {
            as: "span",
            weight: "normal"
          }, contact.companyName()), "?"),
          failureToastProps: {
            text: 'Failed to cancel Accountant Access request',
            testid: 'cancel-acc-access-request-failure'
          },
          onSuccess: refresh,
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13103, {
              customerId,
              companyName
            });
            await api.accountants.cancelRequestAccess(customerId);
          }
        });
      }
    });
  }
  function onRemoveAccountantAccess(contact) {
    showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref6 => {
        let {
          dismissModal
        } = _ref6;
        return /*#__PURE__*/React.createElement(VendorAccountantAccessModal, {
          dismissModal: dismissModal,
          skin: "danger",
          primaryButtonLabel: "Remove access",
          title: "Remove Accountant Access",
          primaryButtonTestid: "remove-acc-access-submit",
          content: /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, null, "Are you sure you want to remove your Accountant Access to", ' ', /*#__PURE__*/React.createElement(Typography, {
            as: "span",
            weight: "normal"
          }, contact.companyName()), "?"), /*#__PURE__*/React.createElement(Typography, null, "Once removed you will no longer be able to access their business.")),
          failureToastProps: {
            text: 'Failed to remove Accountant Access',
            testid: 'remove-acc-access-failure'
          }
          // We fetchUserInfo to remove the accountant workspace from userInfo.workspaces
          ,
          onSuccess: () => Promise.all([refresh(), fetchUserInfo()]),
          onSubmit: async () => {
            const customerId = contact.customerId();
            const companyName = contact.companyName();
            if (!customerId) return;
            logBI(13104, {
              customerId,
              companyName
            });
            await api.accountants.removeMe(customerId);
          }
        });
      }
    });
  }
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "contacts-dashboard",
    ref: scrollElRef,
    header: title,
    subheader: "View contact details",
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, importContactsLink && /*#__PURE__*/React.createElement(Button, {
      skin: "primaryOutline",
      as: Link,
      to: importContactsLink.url,
      "data-testid": "goto-".concat(importContactsLink.id)
    }, importContactsLink.text), /*#__PURE__*/React.createElement(Button, {
      "data-testid": "create-contact-btn",
      prefixIcon: /*#__PURE__*/React.createElement(Plus, {
        height: 18,
        width: 18
      }),
      onClick: () => openContactModal()
    }, "Create contact")),
    loading: isRefreshing,
    onScroll: onScroll
  }, /*#__PURE__*/React.createElement(TableList.Provider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: tableSort,
    onSortChange: onSortChange
  }, hasContacts ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    style: {
      marginBlockEnd: '24px'
    }
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: registerSearchBoxHotkey,
    value: search,
    "data-testid": "contacts-dashboard-search",
    onChange: event => {
      const searchStr = event.target.value;
      changeUrlState({
        search: searchStr
      });
      debouncedLogSearchToBI.onChange(searchStr);
    },
    onClear: onClearSearch
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "contact-status-filter",
    value: contactStatusFilterValue,
    options: contactStatusFilterOptions,
    label: "Contact status",
    onChange: newFilterValue => {
      changeUrlState({
        status: newFilterValue.length ? newFilterValue : contactStatusFilterOptions.map(opt => opt.value)
      });
    }
  })), /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems,
    "data-testid": "contacts-dashboard-table-header"
  })), filteredAndSorted.length ? paginatedData.map(contact => /*#__PURE__*/React.createElement(ContactsDashboardRow, {
    key: contact.id(),
    contact: contact,
    isQboIntegrated: showQboIntegration,
    qboCustomerIdToDisplay: qboCustomerIdToDisplay,
    openContactModal: openContactModal,
    onRequestAccountantAccess: onRequestAccountantAccess,
    onCancelAccountantAccessRequest: onCancelAccountantAccessRequest,
    onRemoveAccountantAccess: onRemoveAccountantAccess,
    onGotoClientPortal: c => enterAccountantAccess(c.customerId(), c.companyName()),
    onRestoreContact: onRestoreContact,
    onArchiveContact: onArchiveContact
  })) : /*#__PURE__*/React.createElement(PageEmptyState, {
    illustration: /*#__PURE__*/React.createElement(EmptyStateIllustration, null),
    title: "No contacts match your request",
    message: "Try adjusting your filters",
    actions: /*#__PURE__*/React.createElement(Button, {
      "data-testid": "contacts-dashboard-empty-search-clear-btn",
      onClick: onClearFilters
    }, "Clear filters")
  })) : /*#__PURE__*/React.createElement(PageEmptyState, {
    "data-testid": "empty-state-page",
    illustration: /*#__PURE__*/React.createElement(EmptyStateIllustration, null),
    message: "No contacts yet. Here you will see your contact list.",
    actions: /*#__PURE__*/React.createElement(CreateProposalButton, {
      "data-testid": "contacts-empty-state-new-btn",
      text: "Create a new agreement",
      hidePrefix: true
    })
  })));
}
export function ContactsDashboard() {
  const {
    fetchContacts
  } = useStoreActions();
  useEffect(() => {
    document.title = title;
  }, []);
  return /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: () => fetchContacts(),
    getStoreLocation: state => state.contacts,
    renderChild: _ref7 => {
      let {
        data,
        loading,
        refresh
      } = _ref7;
      return /*#__PURE__*/React.createElement(ActualContactsDashboard, {
        data: data,
        isRefreshing: loading,
        refresh: refresh
      });
    },
    renderError: _ref8 => {
      let {
        refresh
      } = _ref8;
      return /*#__PURE__*/React.createElement(PageErrorState, {
        "data-testid": "error-state-page",
        title: title,
        onRefresh: refresh
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(PageLoadingState, {
      header: title
    })
  });
}